// @ts-nocheck
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, getDay, parse, set, startOfWeek } from "date-fns";
import { anyPass, defaultTo, equals, has, head, isEmpty, isNil, path, pluck, prop, reject, uniqBy } from "ramda";
import { default as React, useState } from "react";
import { TechnicianResourceView } from "./TechnicianResourceView";
import { CalendarToolbar } from "./CalendarToolbar";
import { add, sub } from "date-fns/fp";
import { AppointmentSummaryDialog, AppointmentSummaryValues } from "./AppointmentSummaryDialog";
import { useToggle } from "../../hooks/useToggle";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useSwipeable } from "react-swipeable";
import { pipe } from "fp-ts/lib/function";
import { isBeyondForwardLimit } from "./calendarLimits";

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});
export const AppointmentCalendar: React.FC<{
  events: any[];
  technicianColors: any;
  refetch;
  onToolbarState?: (state: { view: string; label: React.ReactNode; date?: Date }) => void;
  /** Bump to jump back to today. A counter rather than a callback, so the calendar
   *  keeps owning its own date state. */
  goToTodaySignal?: number;
}> = ({ refetch, events, technicianColors, onToolbarState, goToTodaySignal }) => {
  const [appointmentSummaryValues, setAppointmentValues] = useState<AppointmentSummaryValues>(null);
  const [summaryDialogIsOpen, , toggleSummaryDialog] = useToggle();
  const minDate = set(new Date(), {
    hours: 6,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("day");
  const isNarrow = useMediaQuery("(max-width:600px)");
  const scrollWeek = calendarView === "week" && isNarrow;
  // Memoised: react-big-calendar remounts the toolbar whenever this identity changes,
  // which would wipe the toolbar's state on every render.
  const Toolbar = React.useMemo(() => {
    const ToolbarWithState = (props) => <CalendarToolbar {...props} onToolbarState={onToolbarState} />;
    ToolbarWithState.displayName = "CalendarToolbarWithState";
    return ToolbarWithState;
  }, [onToolbarState]);
  const dayDifference = equals(calendarView, "day") ? 1 : 7;

  const containerRef = React.useRef(null);
  const [navHint, setNavHint] = useState(null);
  const hintTimer = React.useRef(null);

  /**
   * In the scrolling week view a swipe means "scroll the days" until there are no more
   * days to scroll — only then does it mean "change week". Checking the scroll offset
   * is what keeps the two gestures from fighting over the same drag.
   */
  const canNavigate = (direction) => {
    if (!scrollWeek) return true;
    const view = containerRef.current?.querySelector(".rbc-time-view");
    if (!view) return true;
    const furthest = view.scrollWidth - view.clientWidth;
    // 1px of slack: fractional scroll offsets never land exactly on the bound.
    return direction === "prev" ? view.scrollLeft <= 1 : view.scrollLeft >= furthest - 1;
  };

  const flashHint = (direction) => {
    if (!scrollWeek) return;
    setNavHint(direction);
    window.clearTimeout(hintTimer.current);
    hintTimer.current = window.setTimeout(() => setNavHint(null), 700);
  };

  React.useEffect(() => () => window.clearTimeout(hintTimer.current), []);

  // Skips the initial 0 so a fresh mount doesn't reset a date the user just picked.
  React.useEffect(() => {
    if (goToTodaySignal) setSelectedDate(new Date());
  }, [goToTodaySignal]);

  /**
   * Every date change funnels through here so the forward limit cannot be sidestepped
   * by a swipe, an arrow, or anything react-big-calendar decides to call. Backwards
   * navigation is deliberately unbounded — past work is history worth reading.
   */
  const selectDate = (next) => {
    if (isBeyondForwardLimit(next)) return;
    setSelectedDate(next);
  };

  const swipeHandlers = useSwipeable({
    onSwipedRight: () => {
      if (!canNavigate("prev")) return;
      const previousDate = sub({ days: dayDifference }, selectedDate);
      selectDate(previousDate);
      flashHint("prev");
    },
    onSwipedLeft: () => {
      if (!canNavigate("next")) return;
      const nextDate = add({ days: dayDifference }, selectedDate);
      selectDate(nextDate);
      flashHint("next");
    },
  });
  return (
    <div
      className={scrollWeek ? "w-full week-scroll relative" : "w-full"}
      {...swipeHandlers}
      ref={(node) => {
        containerRef.current = node;
        // react-swipeable also wants the ref, so pass it along rather than clobber it.
        swipeHandlers.ref(node);
      }}
    >
      {navHint && (
        <div className="week-nav-hint" data-dir={navHint} aria-hidden="true">
          {navHint === "next" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </div>
      )}
      <Calendar
        localizer={localizer}
        // views={{["day", "agenda", "week", "month"]}}
        // @ts-ignore
        views={{
          day: true,
          // Replaced by the page-level List tab (NUB-359).
          week: false,
          agenda: true,
          month: false,
          assign: TechnicianResourceView,
        }}
        showMultiDayTimes
        events={events}
        view={calendarView}
        onView={setCalendarView}
        date={selectedDate}
        onNavigate={(date) => selectDate(date)}
        onSelectEvent={(event: any) => {
          setAppointmentValues({
            isFixed: event.isFixed,
            // startDate: event.start,
            // endDate: event.end,
            // id: event.id,
            // allDay: event.allDay,
            // technician: event.technician,
            // subject: event.subject,
            // job: event.job
            title: `#${path(["job", "jobNumber"], event)}: ${path(["appointment", "subject"], event)}`,
            // Use time window for display if available, otherwise fall back to appointment start/end
            appointmentStartDate: event.start,
            appointmentEndDate: event.end,
            startTimeWindow: event.startTimeWindow,
            endTimeWindow: event.endTimeWindow,
            customerName: path(["job", "contact", "fullName"], event),
            job: event.job,
            technician: event.technician,
            jobDescription: path(["job", "description"], event),
            serviceLocation: path(["job", "serviceLocation"], event),
          });
          toggleSummaryDialog();
        }}
        min={minDate}
        onSelectSlot={(slotInfo) => {
          setAppointmentValues({
            startDate: slotInfo.start,
            endDate: slotInfo.end,
          });
          toggleSummaryDialog();
        }}
        onRangeChange={async (range) => {
          const startDate = has("start", range) ? prop("start", range) : head(range);
          const startDateRange = pipe(startOfWeek(startDate), sub({ weeks: 1 }));
          const endDateRange = add({ weeks: 3 }, startDateRange);
          await refetch({
            startRange: startDateRange,
            endRange: endDateRange,
          });
        }}
        defaultView={"day"}
        dayPropGetter={(date) => {
          return {
            className: "bg-black",
          };
        }}
        messages={{
          assign: "Assign",
        }}
        slotPropGetter={(date) => {
          return {
            className: "bg-black h-20",
          };
        }}
        eventPropGetter={(event, start, end, isSelected) => {
          // @ts-ignore
          const color = technicianColors[event.technician.id].color;
          const isClosed = event?.job?.status === "Closed";
          return {
            style: {
              backgroundColor: `#${color}`,
              ...(isClosed ? { opacity: 0.5 } : {}),
            },
          };
        }}
        formats={{
          agendaDateFormat: (date, _, localizer) => {
            // @ts-ignore
            return localizer.format(date, "LLL d yyyy");
          },
          dayHeaderFormat: (date, _, localizer) => {
            // @ts-ignore
            return localizer.format(date, "LLL d yyyy");
          },
          timeGutterFormat: (date, _, localizer) => {
            // @ts-ignore
            return localizer.format(date, "h a");
          },
          // hide built-in time labels; we render our own using timeWindow
          eventTimeRangeFormat: () => "",
          eventTimeRangeStartFormat: () => "",
          eventTimeRangeEndFormat: () => "",
        }}
        className="bg-black border border-black"
        startAccessor="start"
        endAccessor="end"
        resourceIdAccessor={"resourceId"}
        resourceAccessor={"technicianId"}
        resourceTitleAccessor={"resourceTitle"}
        components={{
          toolbar: Toolbar,
          event: Event,
        }}
      />
      <AppointmentSummaryDialog
        open={summaryDialogIsOpen}
        handleClose={toggleSummaryDialog}
        appointmentSummaryValues={appointmentSummaryValues}
      />
    </div>
  );
};

const Event = (props) => {
  const { event } = props;
  const job = prop("job", event);
  const isFixed = event?.isFixed;
  const containsDealership = job?.zendeskTags?.some((tag: string) => tag.toLowerCase() === "dealership");

  const windowStart = event?.startTimeWindow ?? event?.timeWindowStart;
  const windowEnd = event?.endTimeWindow ?? event?.timeWindowEnd;

  const renderTimeWindow =
    windowStart && windowEnd ? (
      <div className="rbc-event-label">
        {format(windowStart, "p")} - {format(windowEnd, "p")}
      </div>
    ) : (
      <></>
    );

  if (job) {
    const partsStores = pipe(
      prop("items", job),
      pluck("partsStore"),
      reject(anyPass([isEmpty, isNil])),
      uniqBy(prop("id")),
      defaultTo([])
    );
    const homePartsStoreId = event?.technician?.homePartsStore?.id;
    const partsStoreText = () => {
      if (partsStores?.length > 1) {
        return ` - Multiple Parts Stores`;
      } else {
        if (partsStores?.length === 1 && homePartsStoreId && !equals(prop("id", partsStores[0]), homePartsStoreId)) {
          return " - Alternate Parts Store";
        } else {
          return "";
        }
      }
    };

    return (
      <div>
        {renderTimeWindow}
        <div>
          #{prop("jobNumber", job)}: {event.title} {event.desc && ":  " + event.desc}
        </div>
        <div>
          <strong>
            {isFixed && <span style={{ backgroundColor: "blue" }}> F </span>}
            {containsDealership && <span style={{ backgroundColor: "blue" }}> D </span>}
            {job.status}
            {partsStoreText()}
          </strong>
        </div>
      </div>
    );
  }
  return (
    <div>
      {renderTimeWindow}
      <div>{event.title}</div>
      {event.desc && ":  " + event.desc}
    </div>
  );
};
